import { Node } from 'prosemirror-model';

type TableCellTag = 'td' | 'th';

const getOptionalTableAttributes = (attrs: object): object =>
  Object.entries(attrs).reduce((allAttributes, [attributeName, attributeValue]) => {
    if (attributeValue === undefined || attributeValue === null || attributeValue === '') {
      return allAttributes;
    }
    return {
      ...allAttributes,
      [attributeName]: attributeValue,
    };
  }, {});

const getTableCellAttributes = (dom: HTMLElement): object => ({
  colspan: Math.max(parseInt(dom.getAttribute('colspan') || '1', 10), 1),
  rowspan: Math.max(parseInt(dom.getAttribute('rowspan') || '1', 10), 1),
  style: dom.getAttribute('style'),
  class: dom.getAttribute('class'),
  align: dom.getAttribute('align'),
  valign: dom.getAttribute('valign'),
  width: dom.getAttribute('width'),
});

const tableCellToDOM = (attrs: Node['attrs'], tag: TableCellTag): [TableCellTag, object, number] => [
  tag,
  getOptionalTableAttributes({
    colspan: attrs.colspan > 1 ? attrs.colspan : undefined,
    rowspan: attrs.rowspan > 1 ? attrs.rowspan : undefined,
    style: attrs.style,
    class: attrs.class,
    align: attrs.align,
    valign: attrs.valign,
    width: attrs.width,
  }),
  0,
];

export const tableNode: object = {
  content: 'tableRow+',
  tableRole: 'table',
  group: 'block',
  isolating: true,
  attrs: {
    style: { default: null },
    class: { default: null },
  },
  parseDOM: [
    {
      tag: 'table',
      getAttrs: (dom: HTMLElement) => ({
        style: dom.getAttribute('style'),
        class: dom.getAttribute('class'),
      }),
    },
  ],
  toDOM: ({ attrs }: Node) => ['table', getOptionalTableAttributes(attrs), 0],
};

export const tableRowNode: object = {
  content: '(tableCell|tableHeader)+',
  tableRole: 'row',
  attrs: {
    style: { default: null },
    class: { default: null },
  },
  parseDOM: [
    {
      tag: 'tr',
      getAttrs: (dom: HTMLElement) => ({
        style: dom.getAttribute('style'),
        class: dom.getAttribute('class'),
      }),
    },
  ],
  toDOM: ({ attrs }: Node) => ['tr', getOptionalTableAttributes(attrs), 0],
};

export const tableCellNode: object = {
  content: 'block+',
  attrs: {
    colspan: { default: 1 },
    rowspan: { default: 1 },
    style: { default: null },
    class: { default: null },
    align: { default: null },
    valign: { default: null },
    width: { default: null },
  },
  tableRole: 'cell',
  isolating: true,
  parseDOM: [
    {
      tag: 'td',
      getAttrs: getTableCellAttributes,
    },
  ],
  toDOM: ({ attrs }: Node) => tableCellToDOM(attrs, 'td'),
};

export const tableHeaderNode: object = {
  content: 'block+',
  attrs: {
    colspan: { default: 1 },
    rowspan: { default: 1 },
    style: { default: null },
    class: { default: null },
    align: { default: null },
    valign: { default: null },
    width: { default: null },
  },
  tableRole: 'header_cell',
  isolating: true,
  parseDOM: [
    {
      tag: 'th',
      getAttrs: getTableCellAttributes,
    },
  ],
  toDOM: ({ attrs }: Node) => tableCellToDOM(attrs, 'th'),
};
